import React, { useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Controller } from 'react-hook-form';
import FormToolTip from './FormToolTip';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ErrorMessage } from '@hookform/error-message';
import { StFormDiv } from './FormElements.styles';
import { useDispatch, useSelector } from 'react-redux';
import { updatePrepopulateConfig } from '../../../features/contactInfoSlice';
import { RootState } from '../../../store/store';
import Box from '@mui/material/Box';
import UiFormLabel from '../FormLabel/FormLabel';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isMegaScreenerStudy } from '../../../lib/utils/siteHelper';
import {
  resolvePrePopulateCheckboxField,
  resolveReadOnlyCheckboxField,
} from '../../ContactInfo/prepopulateField';

export const FormInputMultiCheckbox: React.FC<any> = (props) => {
  const {
    name,
    control,
    options,
    toolTip,
    label,
    selectAllText,
    rules,
    errors,
    requiredFieldMessage,
    isReadOnly,
    isMegaScreenerReadOnly,
    prePopulateFromScreener,
    prePopulateFromMegaScreener,
  } = props;

  const dispatch = useDispatch();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const isMegaScreenerSite = isMegaScreenerStudy(siteSettings);
  const activePrePopulateField = resolvePrePopulateCheckboxField(
    isMegaScreenerSite,
    prePopulateFromScreener,
    prePopulateFromMegaScreener
  );
  const activeReadOnlyField = resolveReadOnlyCheckboxField(
    isMegaScreenerSite,
    isReadOnly,
    isMegaScreenerReadOnly
  );
  const prepopulateConfig = useSelector(
    (state: RootState) => state.contactInfoDetails.prepopulateConfig
  );
  const isValueAvailable = useSelector((state: RootState) => state.screenerOutput[name]);

  useEffect(() => {
    dispatch(
      updatePrepopulateConfig({
        [name]: {
          readOnly: activeReadOnlyField?.value,
          prePopulate: activePrePopulateField?.value,
        },
      })
    );
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        validate: (value) => {
          // Ensure at least one checkbox is selected
          return Array.isArray(value.items) && value.items.length > 0
            ? true
            : requiredFieldMessage?.value;
        },
      }}
      render={({ field: { onChange, value = { items: [] }, onBlur } }) => {
        const handleCheckboxChange = (checkedValue: any, keyIndex: any) => {
          const currentValue = Array.isArray(value.items) ? value.items : []; // Ensure value.items is an array
          const newValue = currentValue.some((v: any) => v.id === keyIndex)
            ? { ...value, items: currentValue.filter((v: any) => v.id !== keyIndex) } // Remove the item
            : {
                ...value,
                items: [...currentValue, { id: keyIndex, sitecoreId: '', text: checkedValue }],
              }; // Add the item

          onChange(newValue);
        };

        return (
          <FormControl>
            <UiFormLabel>
              <Text field={label} />
              {toolTip && <FormToolTip value={toolTip} />}
            </UiFormLabel>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
              }}
            >
              {options.map((item: any, index: any) => (
                <FormControlLabel
                  key={index}
                  name={item.key}
                  control={
                    <Checkbox
                      id={item.key ?? item.value}
                      checked={
                        Array.isArray(value.items) &&
                        value.items.some((v: any) => v.id === item.keyIndex)
                      } // Check if the item is in the value.items array
                      onChange={() => handleCheckboxChange(item?.value, item?.keyIndex)}
                      onBlur={onBlur}
                      disabled={
                        isValueAvailable &&
                        prepopulateConfig[name]?.readOnly === true &&
                        prepopulateConfig[name]?.prePopulate === true
                      }
                      sx={{
                        '& .MuiCheckbox-root': {
                          padding: 0,
                        },
                      }}
                    />
                  }
                  label={<Text field={item.label} />}
                  disableTypography={true}
                />
              ))}
            </div>
            {selectAllText && (
              <StFormDiv>
                <RichText field={selectAllText} />
              </StFormDiv>
            )}
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => (
                <Box
                  className="checkboxgroup-error"
                  sx={{
                    color: (theme: import('@mui/material/styles').Theme) =>
                      theme.palette.error.main,
                    fontSize: '14px',
                  }}
                >
                  {message}
                </Box>
              )}
            />
          </FormControl>
        );
      }}
    />
  );
};
